import { FunctionComponent, memo, useCallback, useContext, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../context';
import {
  ChannelAppTabAction,
  StoriesByChannelIdFilter,
  StoryAccess,
  StorySection,
  StoryStatus,
  StoryType,
} from '../../../services';
import {
  Filter,
  FilterGroup,
  FilterValue,
  STORY_TAB_ADMIN_COOKIE_KEY,
  StoryTabType,
  cookieOptions,
  getStorySectionByTabType,
  optArrItem,
  useAppDispatch,
  useAppSelector,
  useAppTabsLabel,
  useFeedFollowBehaviorFilter,
  useFilter,
} from '../../../shared';
import { getStories } from '../../../slices';

import classes from './StoryTabListAdminRightSide.module.scss';

interface StoryTabListAdminRightSideProps {
  storyTabType?: StoryTabType;
}

export const StoryTabListAdminRightSide: FunctionComponent<StoryTabListAdminRightSideProps> = memo(
  ({ storyTabType }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const { getAppTabsLabel } = useAppTabsLabel();

    const { channelId } = useContext(UserContext).userInfo.userData;

    const { query } = useAppSelector(({ stories }) => stories);

    const [cookies, setCookie] = useCookies([STORY_TAB_ADMIN_COOKIE_KEY]);

    const [filter, setFilter] = useState<FilterValue[]>(
      cookies[STORY_TAB_ADMIN_COOKIE_KEY]?.filter ?? []
    );

    const { getGroupValue } = useFilter();

    const { group: feedFollowBehaviorGroup } = useFeedFollowBehaviorFilter();

    const getRequestFilter = useCallback(
      (requestFilter: StoriesByChannelIdFilter) => {
        if (!storyTabType) {
          return { ...requestFilter, query };
        }

        return { ...requestFilter, query, section: getStorySectionByTabType(storyTabType) };
      },
      [query, storyTabType]
    );

    const filterChange = useCallback(
      (filter: FilterValue[]) => {
        const params = { filter, multiCheck: true };

        const section = getGroupValue({ ...params, groupId: 'sections' });
        const status = getGroupValue({ ...params, groupId: 'status' });
        const access = getGroupValue({ ...params, groupId: 'access' });
        const type = getGroupValue({ ...params, groupId: 'type' });
        const feedFollowBehavior = getGroupValue({ ...params, groupId: 'feedFollowBehavior' });

        const requestFilter: StoriesByChannelIdFilter = {
          ...(section?.length && { section }),
          ...(status?.length && { status }),
          ...(type?.length && { type }),
          ...(access?.length && { access }),
          ...(feedFollowBehavior?.length && { feedFollowBehavior }),
        };

        setCookie(STORY_TAB_ADMIN_COOKIE_KEY, { requestFilter, filter }, cookieOptions());

        dispatch(getStories({ channelId, filter: getRequestFilter(requestFilter) }));

        setFilter(filter);
      },
      [channelId, dispatch, getGroupValue, getRequestFilter, setCookie]
    );

    const groups: FilterGroup[] = [
      ...optArrItem(!storyTabType, {
        id: 'sections',
        title: t('common.sections'),
        multiCheck: true,
        allowReset: true,
        items: [
          {
            id: StorySection.WEB_FEED,
            label: `${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.web')}`,
            value: StorySection.WEB_FEED,
          },
          {
            id: StorySection.MOBILE_FEED,
            label: `${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.mobile')}`,
            value: StorySection.MOBILE_FEED,
          },
          {
            id: StorySection.MIX_LIST,
            label: getAppTabsLabel(ChannelAppTabAction.STORIES),
            value: StorySection.MIX_LIST,
          },
          {
            id: StorySection.MIX_LIST_2,
            label: getAppTabsLabel(ChannelAppTabAction.STORIES_2),
            value: StorySection.MIX_LIST_2,
          },
          {
            id: StorySection.MIX_LIST_3,
            label: getAppTabsLabel(ChannelAppTabAction.STORIES_3),
            value: StorySection.MIX_LIST_3,
          },
          {
            id: StorySection.MIX_LIST_4,
            label: getAppTabsLabel(ChannelAppTabAction.STORIES_4),
            value: StorySection.MIX_LIST_4,
          },
          {
            id: StorySection.MIX_LIST_5,
            label: getAppTabsLabel(ChannelAppTabAction.STORIES_5),
            value: StorySection.MIX_LIST_5,
          },
          {
            id: StorySection.PINNED,
            label: getAppTabsLabel(ChannelAppTabAction.PINNED_STORY),
            value: StorySection.PINNED,
          },
          {
            id: StorySection.PINNED_2,
            label: getAppTabsLabel(ChannelAppTabAction.PINNED_STORY_2),
            value: StorySection.PINNED_2,
          },
          {
            id: StorySection.PINNED_3,
            label: getAppTabsLabel(ChannelAppTabAction.PINNED_STORY_3),
            value: StorySection.PINNED_3,
          },
          {
            id: StorySection.PINNED_4,
            label: getAppTabsLabel(ChannelAppTabAction.PINNED_STORY_4),
            value: StorySection.PINNED_4,
          },
          {
            id: StorySection.PINNED_5,
            label: getAppTabsLabel(ChannelAppTabAction.PINNED_STORY_5),
            value: StorySection.PINNED_5,
          },
          {
            id: StorySection.HORIZONTAL,
            label: t(`storySections.${StorySection.HORIZONTAL}`),
            value: StorySection.HORIZONTAL,
          },
          {
            id: StorySection.UNASSIGNED,
            label: t(`storySections.${StorySection.UNASSIGNED}`),
            value: StorySection.UNASSIGNED,
          },
        ],
      }),
      {
        id: 'status',
        title: t('common.status'),
        multiCheck: true,
        allowReset: true,
        items: [
          {
            id: StoryStatus.PUBLISHED,
            label: t('common.published'),
            value: StoryStatus.PUBLISHED,
          },
          {
            id: StoryStatus.UNPUBLISHED,
            label: t('common.unpublished'),
            value: StoryStatus.UNPUBLISHED,
          },
        ],
      },
      {
        id: 'type',
        title: t('common.type'),
        multiCheck: true,
        allowReset: true,
        items: Object.values(StoryType).map((value) => ({
          id: value,
          label: t(`storyType.${value}`),
          value,
        })),
      },
      {
        id: 'access',
        title: t('common.access'),
        multiCheck: true,
        allowReset: true,
        items: [
          {
            id: StoryAccess.EDITOR_LIMITED,
            label: t('roles.editor-limited'),
            value: StoryAccess.EDITOR_LIMITED,
          },
          {
            id: StoryAccess.READER,
            label: t('roles.reader'),
            value: StoryAccess.READER,
          },
        ],
      },
      feedFollowBehaviorGroup,
    ];

    return (
      <div className={classes['story-tab-list-right-side']}>
        <Filter groups={groups} filter={filter} onChange={filterChange} resetPageScrollOnChange />
      </div>
    );
  }
);
